import { isBrowser } from '../helpers/browser';
import { isValidJSON, parseAnyToJSONString, parseStringToJSON } from '../helpers/jsonParser';
import { GAMIFICATION_NOTIFICATIONS, STORE_NAME } from '@intigral/constant';
/**
 * Get the data from local storage
 * @category localStorage
 * @param {string} key The key for the local storage
 * @returns {Object|null|string} The stored data
 */
export const get = (key: string): any => {
    if (!isBrowser()) {
        return null;
    }
    const data = window.localStorage.getItem(key);
    if (!data) {
        return null;
    }
    if (isValidJSON(data)) {
        return parseStringToJSON(data);
    }
    return data;
};

/**
 * Set the localStorage data for a particular key.
 * @category localStorage
 * @param {string} key The key for the local storage
 * @param {any} value The value to store the data. The value can be JSON object, which will be converted to string.
 * @returns {void}
 */
export const set = (key: string, value: any): void => {
    if (!isBrowser()) {
        return;
    }
    if (typeof value !== 'string') {
        value = parseAnyToJSONString(value);
    }
    window.localStorage.setItem(key, value);
};

/**
 * Remove local storage data for a particular key
 * @category localStorage
 * @param {string} key
 * @returns {void}
 */
export const remove = (key: string): void => {
    if (!isBrowser()) {
        return;
    }
    window.localStorage.removeItem(key);
};

/**
 * Clear all data stored in local storage.
 * @category localStorage
 * @returns {void}
 */
export const clearAll = (): void => {
    if (!isBrowser()) {
        return;
    }
    Object.keys(window.localStorage).forEach((key) => {
        if (
            key !== GAMIFICATION_NOTIFICATIONS.STORED_NOTIFICARIONS &&
            key !== GAMIFICATION_NOTIFICATIONS.STORED_BADGES
        ) {
            window.localStorage.removeItem(key);
        }
    });
};

/**
 * Get the data from sessionStorage
 * @category sessionStorage
 * @param {string} key Saved data key
 * @returns {Object|string|null} The JSON decoded data
 */
export const getSession = (key: string): any => {
    if (!isBrowser()) {
        return null;
    }
    const data = window.sessionStorage.getItem(key);
    if (!data) {
        return null;
    }
    if (isValidJSON(data)) {
        return parseStringToJSON(data);
    }
    return data;
};

/**
 * Set data to sessionStorage
 * @category sessionStorage
 * @param {string} key
 * @param {any} value The data to be saved in session storage.
 * @returns {void}
 */
export const setSession = (key: string, value: any): void => {
    if (!isBrowser()) {
        return;
    }
    if (typeof value !== 'string') {
        value = parseAnyToJSONString(value);
    }
    window.sessionStorage.setItem(key, value);
};

/**
 * Remove data from session storage
 * @category sessionStorage
 * @param {string} key The data to be removed from session storage
 * @returns {void}
 */
export const removeSession = (key: string): void => {
    if (!isBrowser()) {
        return;
    }
    window.sessionStorage.removeItem(key);
};

/**
 * Clear all data from session storage
 * @category sessionStorage
 * @returns {void}
 */
export const clearAllSession = (): void => {
    if (!isBrowser()) {
        return;
    }
    window.sessionStorage.clear();
};


export const isCSRFTokenValid = (storeToken: any, csrfToken: any): boolean => {
    if (!csrfToken || !storeToken || !storeToken?.data) {
        return false;
    }
    else if (csrfToken !== storeToken?.data?.token) {
        return false;
    }
    else if (
        storeToken &&
        storeToken.data &&
        storeToken.data?.tokenName &&
        storeToken.expires &&
        storeToken.expires > Date.now()
    ) {
        return true;
    }
    return false;

}

export const setCSRFToken = (data: any) => {
    set(STORE_NAME.API_TOKEN, { data, expires: Date.now() + data.expires * 1000 });
}